import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useGetCustomerPersonasInfiniteScrollQuery } from "./customerPersonaApiSlice";
import { useCreateChatSessionMutation } from "../chat_sessions/chatSessionApiSlice";
import { personaCardImages } from "./personaImages";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useGetAllowancesQuery } from "../chat_sessions/allowancesApiSlice";
import { ModeNotes, gradedNotes } from "../chat_sessions/modeNotes";

import {
  Container,
  Header,
  Button,
  Icon,
  Loader,
  Card,
  Image,
  Popup,
} from "semantic-ui-react";

/**
 * Disclosure required by the client (persona list page, request 2). Both the graded picker
 * and the 사전 연습 screen render the same generated imagery, so both show it (since
 * 2026-10-22 as the footnote of their 유의사항, not in the page header) — exported
 * from here rather than written twice, since a disclosure that drifts between screens is
 * worse than one in a single place.
 */
export const AI_IMAGE_NOTICE = "고객 이미지와 매장 전경은 AI로 생성하였습니다.";

export const CustomerPersonaCard = (props) => {
  const { isMobile } = useBreakpoint();
  // ponytail: no language selector is rendered anywhere, so this is a constant, not
  // state. Bring back useState if a picker is ever added.
  const selectedLanguage = "ko";
  const [pendingRequest, setPendingRequest] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const navigate = useNavigate();

  const [createChatSession, { data: created_chatsession }] = useCreateChatSessionMutation();

  // Check if data is available from the successful mutation and perform the navigation
  // useEffect(() => {
  //   if (created_chatsession) {
  //     navigate(`/chatsessionperform?chatsession_id=${created_chatsession.id}`);
  //   }
  // }, [created_chatsession, navigate]);

  const handleCreateChatSession = async (persona, language) => {
    setPendingRequest(true);
    try {
      // Both entry paths render this card: the 롤플레이 picker (graded) and the 사전 연습
      // screen (practice). The caller decides which, so the mode cannot drift from the
      // screen the trainee thinks they are on.
      const result = await createChatSession({
        "persona": persona,
        "language": language,
        "is_practice": !!props.isPractice,
      }).unwrap();
      navigate(`/audio-orb-openai?session_id=${result.id}`);
    } catch (err) {
      console.error("Failed to create chat session:", err);
    } finally {
      setPendingRequest(false);
    }
  };

  const { hero: heroUrl } = personaCardImages(props.customerPersona);
  // The client's card shows only a number, a title and the situation sentence. Name, gender
  // and age still exist on the persona and still drive the roleplay and the generated
  // images — they are hidden here, not removed.
  const currentActivity = props.customerPersona.persona?.current_activity?.value;
  // `title` (유튜브 크리에이터 etc.) is deliberately NOT rendered: the card identifies a
  // persona only by its number. It is still stored, and still reaches the roleplay system
  // instruction via get_persona_content(), which is what it is for.
  const personaNumber = props.customerPersona.int_id;

  return (
    <div
      onClick={() => handleCreateChatSession(props.customerPersona.id, selectedLanguage)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        cursor: 'pointer',
        backgroundColor: 'white',
        borderRadius: '24px',
        overflow: 'hidden',
        boxShadow: isHovered ? '0 12px 24px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.04)',
        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
        transform: isHovered ? 'translateY(-4px)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: '1px solid rgba(0,0,0,0.05)',
        position: 'relative'
      }}
    >
      {/* Phones: a 16:9 banner instead of a ~1:1.1 portrait, so two or three customers fit on
          a screen instead of one. Framed on the upper part, where the person is; the full scene
          image appears again on the voice page's speak-first hint. */}
      <div style={{ position: 'relative', paddingTop: isMobile ? '56%' : '110%', overflow: 'hidden', backgroundColor: '#f5f5f7' }}>
        {props.customerPersona.is_dynamic && (
          <Popup
            content={
              (() => {
                const aoi = props.customerPersona.areas_of_improvement;
                if (!aoi) return '이 페르소나는 역량 향상을 위해 자동 생성된 맞춤 페르소나입니다';
                const comm = aoi.communication || [];
                const products = aoi.product_knowledge || [];
                if (comm.length === 0 && products.length === 0) return '이 페르소나는 역량 향상을 위해 자동 생성된 맞춤 페르소나입니다';
                return (
                  <div style={{ maxWidth: '280px', lineHeight: 1.5 }}>
                    <div style={{ marginBottom: '6px', fontWeight: 600 }}>이 페르소나는 다음 역량·제품 지식 향상을 위해 생성되었습니다.</div>
                    {comm.length > 0 && (
                      <div style={{ marginBottom: '6px' }}>
                        <div style={{ color: '#86868b', marginBottom: '4px' }}>응대 스킬</div>
                        {comm.map((item, i) => (
                          <div key={i} style={{ marginBottom: '2px' }}>
                            {item.text && <div style={{ fontSize: '0.9em' }}>{item.text}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    {products.length > 0 && (
                      <div>
                        <div style={{ color: '#86868b', marginBottom: '4px' }}>제품 지식</div>
                        <div>{products.map((p, i) => p.name).join(', ')}</div>
                      </div>
                    )}
                  </div>
                );
              })()
            }
            trigger={
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  zIndex: 10,
                  cursor: 'pointer',
                }}
              >
                <Icon name="info circle" style={{ margin: 0, color: 'rgba(128, 128, 128, 0.5)', fontSize: '24px' }} />
              </div>
            }
          />
        )}
        <img
          src={heroUrl}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: isMobile ? 'center 30%' : 'center',
            transition: 'transform 0.5s ease',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)'
          }}
          alt=""
        />
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 60%, transparent 100%)',
          padding: '20px',
          paddingTop: '60px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end'
        }}>
          <h3 style={{ color: 'white', margin: 0, fontSize: '20px', fontWeight: '700', letterSpacing: '-0.01em' }}>
            {personaNumber ? `${personaNumber}번 고객` : '고객'}
          </h3>
        </div>
      </div>
      
      <div style={{ padding: isMobile ? '14px 16px' : '20px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        {/* The situation an employee walks up to, in the client's own wording. Absent on
            personas created before the field existed, so it renders nothing rather than an
            empty row. current_location is no longer shown — it only feeds the scene image. */}
        {currentActivity && (
          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                fontSize: '13px',
                color: '#424245',
                lineHeight: '1.5',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {currentActivity}
            </div>
          </div>
        )}

        <Button 
          fluid 
          style={{ 
            borderRadius: '14px', 
            backgroundColor: isHovered ? '#007AFF' : '#F2F2F7',
            color: isHovered ? 'white' : '#1d1d1f',
            fontWeight: '600',
            boxShadow: 'none',
            transition: 'all 0.2s',
            border: 'none',
            padding: '12px'
          }}
          loading={pendingRequest}
        >
          시작하기
        </Button>
      </div>
    </div>
  );
};

const CustomerPersonaCards = () => {
  // Exempt users (admins) get no `graded` key and see no count.
  const { data: allowances } = useGetAllowancesQuery();
  const graded = allowances?.graded;
  const { isMobile } = useBreakpoint();
  const [page, setPage] = useState(1);
  const { data, isFetching, error } = useGetCustomerPersonasInfiniteScrollQuery(page);
  const personas = data?.results ?? [];
  const hasNextPage = Boolean(data?.next);

  useEffect(() => {
    const onScroll = () => {
      const scrolledToBottom =
        window.innerHeight + window.scrollY >= document.body.scrollHeight * 0.8;

      // only page forward while the API reports a next page — otherwise page+1
      // hits DRF's 404 "Invalid page" and trips the error toast.
      if (scrolledToBottom && !isFetching && !error && hasNextPage) {
        setPage(page + 1);
      }
    };

    document.addEventListener("scroll", onScroll);

    return function () {
      document.removeEventListener("scroll", onScroll);
    };
  }, [page, isFetching, error, hasNextPage]);

  if (personas.length === 0 && isFetching) {
    return <Loader active />;
  }


  return (
    <div style={{ backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header Section */}
      <div style={{ 
        padding: '16px 0', 
        backgroundColor: 'rgba(255, 255, 255, 0.9)', 
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(0,0,0,0.05)'
      }} className="page-header-band">
        <Container>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="header-icon-desktop-only" style={{ 
              width: '40px', 
              flexShrink: 0, // stays a circle when the text beside it wraps
              height: '40px', 
              borderRadius: '50%', 
              backgroundColor: '#F2F2F7', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Icon name="users" style={{ margin: 0, color: '#007AFF', fontSize: '18px' }} />
            </div>
            <div>
              {/* Same name as the /simulation card that leads here: this list starts a graded attempt. */}
              <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, color: '#1d1d1f' }}>롤플레이 평가 응시</h2>
              <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>평가를 진행할 고객을 선택하세요.</p>
              {graded && (
                <p style={{ fontSize: '13px', color: '#007AFF', fontWeight: 600, margin: '6px 0 0' }}>
                  <Icon name="check circle outline" style={{ margin: '0 4px 0 0' }} />
                  남은 응시 횟수 {graded.attempts_remaining}회 / {graded.attempts_total}회
                </p>
              )}
            </div>
          </div>
        </Container>
      </div>

      <Container>
        <ModeNotes notes={gradedNotes(graded)} footnote={AI_IMAGE_NOTICE} style={{ marginBottom: isMobile ? '14px' : '20px' }} />
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: isMobile ? '14px' : '24px',
          paddingBottom: '60px'
        }}>
          {personas.map((p, i) => {
            // Keyed by id, not index: identity now comes from the persona itself.
            return <CustomerPersonaCard key={p.id} customerPersona={p} />;
          })}
        </div>
        
        {!error && isFetching && (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Loader active inline />
          </div>
        )}
      </Container>
    </div>
  );
};

export default CustomerPersonaCards;
